import * as React from 'react'

import {useFormik} from 'formik'
import invariant from 'invariant'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import * as Yup from 'yup'

import useAsync from '../../../../commons/src/hooks/use-async'
import {captureException} from '../../../../commons/src/services/logging'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import useSelector from '../../hooks/use-selector'
import * as providerServiceOfferApi from '../../services/api/provider-service-offer'
import {toTouchedFormData} from '../utils/form'

import {ProviderServiceOfferOption} from '../../../../commons/src/types'
import {ProviderId} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import Stack from '../../../../commons/src/components/stack'

export const serviceOfferOptions: {id: ProviderServiceOfferOption; name: string}[] = [
  {id: '3d_printing', name: '3D Printing'},
  {id: 'cnc_machining', name: 'CNC Machining'},
  {id: 'vacuum_casting', name: 'Vacuum Casting'},
  {id: 'sheet_metal_fabrication', name: 'Sheet Metal Fabrication'},
  {id: 'injection_molding', name: 'Injection Molding'},
  {id: 'tapping_and_threading', name: 'Tapping and Threading'},
  {id: 'welding', name: 'Welding'},
  {id: '3d_design_services', name: '3D Design Services'},
]

const initialServicesValues: Record<ProviderServiceOfferOption, boolean> = {
  '3d_printing': false,
  cnc_machining: false,
  vacuum_casting: false,
  sheet_metal_fabrication: false,
  injection_molding: false,
  tapping_and_threading: false,
  welding: false,
  '3d_design_services': false,
}

type Props = {
  providerId: ProviderId
}

const SettingsProfileServicesPartial = ({providerId}: Props) => {
  const [isProviderServicesSubmitted, setIsProviderServicesSubmitted] = React.useState(false)

  const provider = useSelector(state => state.core.provider)
  invariant(provider, 'Provider missing.')

  const {
    isLoading: isLoadingProviderServices,
    data: providerServices = [],
    refresh: receiveProviderServices,
    error: providerServicesError,
  } = useAsync(() => providerServiceOfferApi.getByProvider(provider.id), [], {
    onError: error => {
      captureException(error)
    },
  })

  const memoizedInitialServicesValues = React.useMemo(
    () => ({
      ...initialServicesValues,
      ...providerServices.reduce(
        (acc, service) => {
          acc[service.service] = true
          return acc
        },
        {} as Record<ProviderServiceOfferOption, boolean>
      ),
      formikGeneral: '',
    }),
    [providerServices]
  )

  const providerServicesFormik = useFormik<
    Record<ProviderServiceOfferOption, boolean> & {formikGeneral: string}
  >({
    enableReinitialize: true,
    initialValues: {
      ...initialServicesValues,
      ...providerServices.reduce(
        (acc, service) => {
          acc[service.service] = true
          return acc
        },
        {} as Record<ProviderServiceOfferOption, boolean>
      ),
      formikGeneral: '',
    },
    validationSchema: Yup.object({
      ...Object.keys(initialServicesValues).reduce(
        (acc, key) => {
          acc[key] = Yup.boolean().required()
          return acc
        },
        {} as Record<string, Yup.Schema<boolean>>
      ),
    }),
    onSubmit: async (values, actions) => {
      const touchedValues = omit(toTouchedFormData(values, memoizedInitialServicesValues), [
        'formikGeneral',
      ]) as Record<string, boolean>

      const idsToDelete = providerServices
        .filter(item => touchedValues[item.service] === false)
        .map(item => item.id)

      const servicesToCreate = Object.keys(touchedValues)
        .filter(key => touchedValues[key])
        .map(key => ({
          service: key as ProviderServiceOfferOption,
          provider: providerId,
        }))

      try {
        await Promise.all([
          providerServiceOfferApi.removeMultiple(idsToDelete),
          providerServiceOfferApi.createMultiple(servicesToCreate),
        ])

        setIsProviderServicesSubmitted(true)
        receiveProviderServices()
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  return (
    <Section>
      <Stack>
        <Headline label="Service Offer" classNames={['u-no-margin-bottom']} />
        <Paragraph classNames={['u-no-margin-bottom']}>
          Please select which services you offer.
        </Paragraph>
        {isLoadingProviderServices && (
          <div>
            <LoadingIndicator />
          </div>
        )}
        {!isLoadingProviderServices && providerServicesError && (
          <ActionBar>
            <Paragraph size="xs" warning classNames={['u-no-margin-bottom']}>
              Loading your current specialization settings failed
            </Paragraph>
            <LinkButton
              icon={<Icon source={refreshIcon} />}
              onClick={() => {
                receiveProviderServices()
              }}
            />
          </ActionBar>
        )}
        {!isLoadingProviderServices && !providerServicesError && (
          <form onSubmit={providerServicesFormik.handleSubmit}>
            {serviceOfferOptions.map(service => (
              <LabeledCheckbox
                key={service.id}
                tiny
                baseline
                name={service.id}
                label={service.name}
                checked={providerServicesFormik.values[service.id]}
                error={
                  providerServicesFormik.submitCount > 0 &&
                  !!providerServicesFormik.errors[service.id]
                }
                onChange={(value, name) => {
                  providerServicesFormik.setFieldValue(name as string, value)
                  if (isProviderServicesSubmitted) {
                    setIsProviderServicesSubmitted(false)
                  }
                }}
                onBlur={providerServicesFormik.handleBlur}
              />
            ))}
            {providerServicesFormik.errors.formikGeneral && (
              <Paragraph error>{providerServicesFormik.errors.formikGeneral}</Paragraph>
            )}
          </form>
        )}
        <ActionBar>
          <Button
            disabled={
              providerServicesFormik.isSubmitting ||
              isProviderServicesSubmitted ||
              !providerServicesFormik.dirty
            }
            icon={
              isProviderServicesSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerServicesFormik.isSubmitting &&
                providerServicesFormik.submitCount > 0 &&
                isEmpty(providerServicesFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerServicesFormik.handleSubmit())}
          >
            {isProviderServicesSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={
              providerServicesFormik.isSubmitting ||
              isProviderServicesSubmitted ||
              !providerServicesFormik.dirty
            }
            onClick={preventDoubleClick(() => {
              providerServicesFormik.resetForm()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )
}

export default SettingsProfileServicesPartial
